// Core Modules
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// External Modules
import { catchError, map, Observable, take } from 'rxjs';

// Services
import { AuthService } from '@core/services/auth/auth.service';
import { ConfigService } from '@core/services/config/config.service';
import { CryptoService } from '@core/services/crypto/crypto.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';

// Models
import { AppEndpointsModel } from '@shared/models/endpoints.config.model';
import { AppEnvironmentConfig } from '@shared/models/environment-config.model';
import { ClientMarketingAutomationModel } from '@shared/models/client-marketing-automation.model';
import { IUserSettingsUsageReportURL } from '@shared/models/user-settings/user-settings-usage-report.model';

// Enums
import { LocalStorageEnum } from '@shared/enum/local-storage-keys.enum';

// Environment
import { environment } from '@env/environment';

@Injectable()
export class UserSettingsService {
  // Injectors
  private readonly authService = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly cryptoService = inject(CryptoService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);

  // Private Members
  private readonly appEndpoints: AppEndpointsModel;
  private readonly appEnvironment: AppEnvironmentConfig;

  constructor() {
    this.appEnvironment = environment;
    this.appEndpoints = this.configService.getEndpointsByModule('appEndpoints');
  }

  public getEloquaURL(): string {
    const auth0Domain = localStorage.getItem(LocalStorageEnum.AUTH0_DOMAIN);
    if (auth0Domain && this.appEnvironment.SAML_PROVIDER && this.appEnvironment.ELOQUA_CLIENT_ID) {
      let uriParams = `?hg-environment=${this.appEnvironment.HG_ENVIRONMENT}`;
      const userDetails = this.authService.getUserProfile();
      if (userDetails && userDetails['hg-customer']) {
        const hgCustomerCode = this.cryptoService.decrypt(String(userDetails['hg-customer'].clientId));
        uriParams += `&hg-customer-code=${hgCustomerCode}`;
      }
      const eloquaLink = `https://${auth0Domain}/${this.appEnvironment.SAML_PROVIDER}/${this.appEnvironment.ELOQUA_CLIENT_ID}${uriParams}`;
      return eloquaLink;
    } else {
      return '';
    }
  }

  public getMarketingAutomationConfig(): Observable<ClientMarketingAutomationModel[]> {
    const API_URL = `${this.appEnvironment.API}${this.appEndpoints.marketingAutomation}`;
    return this.httpClient.get<ClientMarketingAutomationModel[]>(API_URL).pipe(
      take(1),
      map((data: ClientMarketingAutomationModel[]) => data),
      catchError(this.errorHandlerService.errorHandler)
    );
  }

  public getUsageReportUrl(): Observable<IUserSettingsUsageReportURL> {
    const API_URL = `${this.appEnvironment.API}${this.appEndpoints.usageReport}`;
    return this.httpClient.get<IUserSettingsUsageReportURL>(API_URL).pipe(
      take(1),
      map((data: IUserSettingsUsageReportURL) => data),
      catchError(this.errorHandlerService.errorHandler)
    );
  }
}
